import { useEffect, useId, useRef, useState } from "react";
import isEqual from "lodash/isEqual";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import styles from "@/components/documentTemplate/TemplateParameterEditor.module.css";
import { parameterControlsSx } from "@/components/documentTemplate/parameterUiStyles";
import { useTranslation } from "react-i18next";
import TemplateParameterFields from "@/components/documentTemplate/TemplateParameterFields";
import {
  normalizeParameterValue,
  parameterValueError,
} from "@/components/documentTemplate/parameterValues";
import type {
  DocumentTemplateParameter,
  ParameterDefaultRule,
  ParameterType,
} from "@/types/DocumentTemplateParameter";

const types: ParameterType[] = [
  "text",
  "integer",
  "decimal",
  "date",
  "boolean",
  "select",
];
const rules: { value: ParameterDefaultRule; types: ParameterType[] }[] = [
  { value: "today", types: ["date"] },
  { value: "current_year", types: ["integer"] },
  { value: "previous_quarter_year", types: ["integer"] },
  { value: "previous_quarter_number", types: ["integer", "select"] },
];

interface Props {
  value: DocumentTemplateParameter[];
  onChange: (value: DocumentTemplateParameter[]) => void;
  onDraftDirtyChange?: (dirty: boolean) => void;
}

export default function TemplateParameterEditor({
  value,
  onChange,
  onDraftDirtyChange,
}: Props) {
  const { t } = useTranslation();
  const dialogTitleId = useId();
  const [draft, setDraft] = useState<DocumentTemplateParameter | null>(null);
  const [index, setIndex] = useState<number | null>(null);
  const initialDraft = useRef<DocumentTemplateParameter | null>(null);
  const closePending = useRef(false);
  const { showConfirmationModalAsync } = useConfirmationModal();
  const draftDirty = draft !== null && !isEqual(draft, initialDraft.current);
  useEffect(() => {
    onDraftDirtyChange?.(draftDirty);
    return () => onDraftDirtyChange?.(false);
  }, [draftDirty, onDraftDirtyChange]);
  const requestClose = async () => {
    if (closePending.current) return;
    closePending.current = true;
    try {
      if (
        !draftDirty ||
        (await showConfirmationModalAsync(t("unsaved_formChanges_confirm")))
      )
        setDraft(null);
    } finally {
      closePending.current = false;
    }
  };
  const [error, setError] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const begin = (parameter?: DocumentTemplateParameter, position?: number) => {
    const next = parameter
      ? structuredClone(parameter)
      : { name: "", label: "", type: "text" as const, required: false };
    initialDraft.current = structuredClone(next);
    setDraft(next);
    setIndex(position ?? null);
    setError(false);
  };
  const update = (changes: Partial<DocumentTemplateParameter>) =>
    setDraft((previous) => (previous ? { ...previous, ...changes } : previous));
  const apply = () => {
    if (!draft) return;
    const normalized = JSON.parse(
      JSON.stringify(draft)
    ) as DocumentTemplateParameter;
    if (normalized.default !== undefined)
      normalized.default = normalizeParameterValue(
        normalized,
        normalized.default
      );
    const invalid =
      !/^[a-z][a-z0-9_]{0,63}$/.test(draft.name) ||
      !draft.label.trim() ||
      ["constructor", "prototype", "items", "keys", "values", "get"].includes(
        draft.name
      ) ||
      value.some(
        (parameter, position) =>
          position !== index && parameter.name === draft.name
      ) ||
      (draft.min !== undefined &&
        draft.max !== undefined &&
        draft.min > draft.max) ||
      (draft.type === "select" &&
        (!draft.choices?.length ||
          draft.choices.some(
            (choice) =>
              !choice.label.trim() ||
              choice.value === "" ||
              (typeof choice.value === "number" &&
                !Number.isFinite(choice.value))
          ) ||
          new Set(
            draft.choices.map(
              (choice) => `${typeof choice.value}:${choice.value}`
            )
          ).size !== draft.choices.length)) ||
      (normalized.default !== undefined &&
        (normalized.default === null ||
          !!parameterValueError(
            { ...normalized, required: true },
            normalized.default
          )));
    if (invalid) {
      setError(true);
      return;
    }
    onChange(
      index === null
        ? [...value, normalized]
        : value.map((parameter, position) =>
            position === index ? normalized : parameter
          )
    );
    setDraft(null);
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        ...parameterControlsSx,
        border: "1px solid #dfe5e9",
        borderRadius: "var(--radius-large, 1rem) !important",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: "#71717a" }} />}
      >
        <Box className={styles.parameterHeading}>
          <span>{t("document_parameters.title")}</span>
          <span className={styles.typeBadge}>{value.length}</span>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2 }}>
        <div className={styles.parameterList}>
          {copyError && (
            <Alert severity="error">
              {t("document_parameters.copy_failed")}
            </Alert>
          )}
          {value.map((parameter, position) => (
            <div key={parameter.name} className={styles.parameterRow}>
              <div className={styles.parameterInfo}>
                <div className={styles.parameterHeading}>
                  <span>{parameter.label}</span>
                  <span className={styles.typeBadge}>
                    {t(`document_parameters.types.${parameter.type}`)}
                  </span>
                </div>
                <Button
                  size="small"
                  startIcon={
                    <ContentCopyOutlinedIcon
                      sx={{ fontSize: "14px !important" }}
                    />
                  }
                  data-variant="copy"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        `{{ parameters.${parameter.name} }}`
                      );
                      setCopyError(false);
                    } catch {
                      setCopyError(true);
                    }
                  }}
                >
                  <code
                    className={styles.expression}
                  >{`{{ parameters.${parameter.name} }}`}</code>
                </Button>
              </div>
              <div className={styles.rowActions}>
                <Button
                  aria-label={t("document_parameters.edit")}
                  title={t("document_parameters.edit")}
                  sx={{ minWidth: 36, px: "8px !important" }}
                  onClick={() => begin(parameter, position)}
                >
                  <EditOutlinedIcon fontSize="small" />
                </Button>
                <Button
                  aria-label={t("document_parameters.remove")}
                  title={t("document_parameters.remove")}
                  data-variant="danger"
                  sx={{ minWidth: 36, px: "8px !important" }}
                  onClick={() =>
                    onChange(value.filter((_, entry) => entry !== position))
                  }
                >
                  <DeleteOutlineIcon fontSize="small" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            data-variant="outline"
            startIcon={<AddIcon fontSize="small" />}
            sx={{ alignSelf: "flex-start" }}
            onClick={() => begin()}
            disabled={value.length >= 50}
          >
            {t("document_parameters.add")}
          </Button>
        </div>
      </AccordionDetails>
      <Dialog
        open={draft !== null}
        onClose={requestClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby={dialogTitleId}
        PaperProps={{
          sx: {
            ...parameterControlsSx,
            borderRadius: "1.5rem",
            border: "1px solid #dfe5e9",
            boxShadow: "0 16px 48px rgb(0 0 0 / 12%)",
          },
        }}
      >
        <DialogTitle
          id={dialogTitleId}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "1rem",
            fontWeight: 500,
            p: "20px 24px",
          }}
        >
          {t(
            index === null
              ? "document_parameters.add"
              : "document_parameters.edit"
          )}
          <Button
            aria-label={t("close")}
            sx={{ minWidth: 32, p: "4px !important" }}
            onClick={requestClose}
          >
            <CloseIcon fontSize="small" />
          </Button>
        </DialogTitle>
        {draft && (
          <>
            <DialogContent>
              <Stack spacing={2.5} sx={{ pt: 0.5 }}>
                {error && (
                  <Alert severity="error">
                    {t("document_parameters.invalid_definition")}
                  </Alert>
                )}
                <div className={styles.twoColumns}>
                  <TextField
                    label={t("document_parameters.name")}
                    value={draft.name}
                    onChange={(event) => update({ name: event.target.value })}
                    required
                  />
                  <TextField
                    label={t("document_parameters.label")}
                    value={draft.label}
                    onChange={(event) => update({ label: event.target.value })}
                    required
                  />
                </div>
                <TextField
                  label={t("document_parameters.type")}
                  select
                  value={draft.type}
                  onChange={(event) =>
                    setDraft({
                      name: draft.name,
                      label: draft.label,
                      help_text: draft.help_text,
                      required: draft.required,
                      type: event.target.value as ParameterType,
                      ...(event.target.value === "select"
                        ? { choices: [] }
                        : {}),
                    })
                  }
                >
                  {types.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`document_parameters.types.${type}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t("document_parameters.help_text")}
                  value={draft.help_text ?? ""}
                  onChange={(event) =>
                    update({ help_text: event.target.value })
                  }
                />
                <FormControlLabel
                  label={t("document_parameters.required")}
                  control={
                    <Checkbox
                      checked={!!draft.required}
                      onChange={(_, required) => update({ required })}
                    />
                  }
                />
                {draft.type === "select" && (
                  <Stack spacing={1}>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 400 }}>
                      {t("document_parameters.choices")}
                    </Typography>
                    {draft.choices?.map((choice, choiceIndex) => (
                      <div className={styles.choiceRow} key={choiceIndex}>
                        <TextField
                          label={t("document_parameters.label")}
                          value={choice.label}
                          onChange={(event) =>
                            update({
                              choices: draft.choices?.map((entry, position) =>
                                position === choiceIndex
                                  ? { ...entry, label: event.target.value }
                                  : entry
                              ),
                            })
                          }
                        />
                        <TextField
                          label={t("document_parameters.value")}
                          value={choice.value}
                          type={
                            typeof choice.value === "number" ? "number" : "text"
                          }
                          onChange={(event) =>
                            update({
                              choices: draft.choices?.map((entry, position) =>
                                position === choiceIndex
                                  ? {
                                      ...entry,
                                      value:
                                        typeof choice.value === "number"
                                          ? Number(event.target.value)
                                          : event.target.value,
                                    }
                                  : entry
                              ),
                            })
                          }
                        />
                        <TextField
                          select
                          label={t("document_parameters.type")}
                          value={typeof choice.value}
                          onChange={(event) =>
                            update({
                              choices: draft.choices?.map((entry, position) =>
                                position === choiceIndex
                                  ? {
                                      ...entry,
                                      value:
                                        event.target.value === "number"
                                          ? 0
                                          : String(entry.value),
                                    }
                                  : entry
                              ),
                            })
                          }
                        >
                          <MenuItem value="string">
                            {t("document_parameters.types.text")}
                          </MenuItem>
                          <MenuItem value="number">
                            {t("document_parameters.types.decimal")}
                          </MenuItem>
                        </TextField>
                        <Button
                          data-variant="danger"
                          aria-label={t("document_parameters.remove")}
                          title={t("document_parameters.remove")}
                          sx={{ minWidth: 36, px: "8px !important" }}
                          onClick={() =>
                            update({
                              choices: draft.choices?.filter(
                                (_, position) => position !== choiceIndex
                              ),
                            })
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      data-variant="outline"
                      startIcon={<AddIcon fontSize="small" />}
                      sx={{ alignSelf: "flex-start" }}
                      onClick={() =>
                        update({
                          choices: [
                            ...(draft.choices ?? []),
                            { value: "", label: "" },
                          ],
                        })
                      }
                    >
                      {t("document_parameters.add_choice")}
                    </Button>
                  </Stack>
                )}
                {(["integer", "decimal"].includes(draft.type)
                  ? (["min", "max"] as const)
                  : []
                ).map((bound) => (
                  <TextField
                    key={bound}
                    type="number"
                    label={t(`document_parameters.${bound}`)}
                    value={draft[bound] ?? ""}
                    onChange={(event) =>
                      update({
                        [bound]:
                          event.target.value === ""
                            ? undefined
                            : Number(event.target.value),
                      })
                    }
                  />
                ))}
                <TextField
                  select
                  label={t("document_parameters.default")}
                  value={
                    draft.default_rule ??
                    (draft.default !== undefined ? "fixed" : "none")
                  }
                  onChange={(event) => {
                    const rest = { ...draft };
                    delete rest.default;
                    delete rest.default_rule;
                    setDraft({
                      ...rest,
                      ...(event.target.value === "fixed"
                        ? { default: draft.type === "boolean" ? false : "" }
                        : event.target.value === "none"
                        ? {}
                        : {
                            default_rule: event.target
                              .value as ParameterDefaultRule,
                          }),
                    });
                  }}
                >
                  <MenuItem value="none">
                    {t("document_parameters.no_default")}
                  </MenuItem>
                  <MenuItem value="fixed">
                    {t("document_parameters.fixed_default")}
                  </MenuItem>
                  {rules
                    .filter((rule) => rule.types.includes(draft.type))
                    .map((rule) => (
                      <MenuItem key={rule.value} value={rule.value}>
                        {t(`document_parameters.rules.${rule.value}`)}
                      </MenuItem>
                    ))}
                </TextField>
                {draft.default !== undefined && (
                  <TemplateParameterFields
                    definitions={[
                      {
                        ...draft,
                        name: "default",
                        label: t("document_parameters.default"),
                        required: false,
                      },
                    ]}
                    values={{ default: draft.default }}
                    onChange={(_, defaultValue) =>
                      update({ default: defaultValue })
                    }
                  />
                )}
              </Stack>
            </DialogContent>
            <DialogActions
              sx={{ px: 3, py: 2, borderTop: "1px solid #f4f4f5", gap: 1 }}
            >
              <Button data-variant="outline" onClick={requestClose}>
                {t("cancel")}
              </Button>
              <Button data-variant="primary" onClick={apply}>
                {t("document_parameters.apply")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Accordion>
  );
}
